import React, { useState } from "react";
import { toast } from "sonner";
import { ChevronUp, ChevronDown, X, Plus, Save, Loader2 } from "lucide-react";
import { api, formatApiError } from "../lib/api";

/**
 * Reusable milestone templates editor.
 *
 * Props:
 *  - title, subtitle (strings) — display copy
 *  - initial (string[]) — starting list
 *  - saveEndpoint (string) — PUT endpoint, expects `{templates: [...]}`
 *  - onSaved (fn) — invoked with the returned templates after save
 */
export default function MilestoneTemplatesEditor({ title, subtitle, initial = [], saveEndpoint, onSaved }) {
  const [items, setItems] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState("");

  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (items.some((x) => x.toLowerCase() === v.toLowerCase())) {
      toast.error("Milestone already in the list");
      return;
    }
    setItems([...items, v]);
    setDraft("");
  };
  const update = (idx, value) => setItems((prev) => prev.map((v, i) => (i === idx ? value : v)));
  const remove = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    setItems((prev) => {
      const arr = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };
  const save = async () => {
    const cleaned = items.map((x) => x.trim()).filter(Boolean);
    if (!cleaned.length) { toast.error("Add at least one milestone"); return; }
    setBusy(true);
    try {
      const { data } = await api.put(saveEndpoint, { templates: cleaned });
      toast.success("Saved");
      setItems(data.templates);
      onSaved && onSaved(data.templates);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <div className="card-soft" data-testid="milestone-templates-editor">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="min-w-0">
          <h3 className="font-display text-2xl font-bold text-[#2C2A29]">{title}</h3>
          {subtitle && <p className="text-sm text-[#7A7571] mt-1">{subtitle}</p>}
        </div>
        <button onClick={save} disabled={busy} data-testid="milestones-save-btn"
          className="btn-primary inline-flex items-center gap-2 flex-shrink-0">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {busy ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="space-y-2 mb-4">
        {items.length === 0 && (
          <div className="text-sm text-[#7A7571] italic p-3">No milestones yet — add one below.</div>
        )}
        {items.map((name, idx) => (
          <div key={idx}
            className="flex items-center gap-2 bg-[#FBF7EF] rounded-2xl px-3 py-2 border border-[#EBE5D9]"
            data-testid={`ms-tpl-row-${idx}`}
          >
            <span className="text-xs text-[#7A7571] font-mono w-6 text-center">{idx + 1}</span>
            <div className="flex flex-col">
              <button
                type="button"
                onClick={() => move(idx, -1)}
                disabled={idx === 0}
                data-testid={`ms-tpl-up-${idx}`}
                className="btn-ghost !p-1 disabled:opacity-30"
                title="Move up"
              >
                <ChevronUp size={14} />
              </button>
              <button
                type="button"
                onClick={() => move(idx, 1)}
                disabled={idx === items.length - 1}
                data-testid={`ms-tpl-down-${idx}`}
                className="btn-ghost !p-1 disabled:opacity-30"
                title="Move down"
              >
                <ChevronDown size={14} />
              </button>
            </div>
            <input
              className="input-soft !py-2 !px-3 flex-1"
              value={name}
              onChange={(e) => update(idx, e.target.value)}
              data-testid={`ms-tpl-input-${idx}`}
            />
            <button type="button" onClick={() => remove(idx)}
              data-testid={`ms-tpl-remove-${idx}`}
              className="btn-ghost !p-1.5 text-[#9F1239]" title="Remove">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <input
          className="input-soft flex-1"
          placeholder="e.g. Tiling"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          data-testid="ms-tpl-new-input"
        />
        <button type="button" onClick={add} data-testid="ms-tpl-add-btn"
          className="btn-secondary inline-flex items-center gap-2">
          <Plus size={16} /> Add
        </button>
      </div>
    </div>
  );
}
